import { useEffect, useRef } from 'react';
import { useAtom, useStore } from 'jotai';
import { DOCUMENT_PAGE_CONTENT_CLASS } from '../utils';
import { activeDrawerAtom } from './atoms/active-drawer';

// Every drawer using this hook is a slideIn SidePanel that pushes the page aside:
// the document page (its header included) when present, else the structuring
// shell alone. querySelector returns the first match in document order, so the
// wrapping document page wins.
export const PAGE_CONTENT_SELECTOR = `.${DOCUMENT_PAGE_CONTENT_CLASS}, .structuring-shell`;

// Outlasts Carbon's slide-in margin transition (~240ms).
const PUSH_SETTLE_MS = 400;

/** Lets the page content back once no drawer pushes it aside. */
const clearPageContentMargin = () => {
  const content = document.querySelector<HTMLElement>(PAGE_CONTENT_SELECTOR);
  if (content) content.style.marginInlineEnd = '0';
};

/**
 * Coordinates the structuring SidePanel set so only one is open at a time. While this drawer is
 * open it owns `activeDrawerAtom`; if another drawer claims the atom, this one calls `onClose` and
 * yields the slot.
 *
 * Carbon zeroes the page content's margin whenever a panel with a selector is closed — including
 * one that merely mounts closed, or one closing because another drawer took over — which would
 * wipe the margin the open drawer set. So a closed panel gets no selector, and the hook clears the
 * margin itself when its drawer gives up the slot without handing it to another drawer.
 *
 * @param {string} key - A stable name for the drawer.
 * @param {boolean} open - The panel's open state.
 * @param {Function} onClose - Closes the panel.
 * @return {string | undefined} The SidePanel's `selectorPageContent` (and the resize handle's
 *   `pageContentSelector`), set only while open.
 */
export function useExclusiveDrawer(
  key: string,
  open: boolean,
  onClose: () => void
): string | undefined {
  const [active, setActive] = useAtom(activeDrawerAtom);
  const store = useStore();
  // The owner we last saw while open. On the render where we open, `active`
  // still names the drawer we are replacing (our claim hasn't landed yet), so
  // only a claim by some other drawer after that counts as a takeover.
  const ownerSeen = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    if (open) setActive(key);
  }, [open, key, setActive]);

  useEffect(() => {
    if (!open) {
      ownerSeen.current = undefined;
      return;
    }
    if (ownerSeen.current === undefined || active === key) {
      ownerSeen.current = active;
      return;
    }
    if (active && active !== ownerSeen.current) onClose();
  }, [open, key, active, onClose]);

  // Closed while still owning the slot (no one took over): release it and let
  // the page content back.
  useEffect(() => {
    if (open || active !== key) return;
    setActive(null);
    clearPageContentMargin();
  }, [open, key, active, setActive]);

  // Unmounted while owning the slot — e.g. PartyDetail swapping its panels for
  // a loading state — so the SidePanel never rendered closed. Read the store:
  // the render-time `active` may be stale by now.
  useEffect(() => {
    return () => {
      if (store.get(activeDrawerAtom) !== key) return;
      setActive(null);
      clearPageContentMargin();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Releases the slot only on unmount or a key change.
  }, [key]);

  // Carbon's horizontal Tabs re-measure their overflow chevrons only on a
  // window resize, and the push narrows the page without one. While this drawer
  // pushes the page, and through its close transition, a change in the page
  // content's width is announced as a window resize.
  useEffect(() => {
    const content = document.querySelector<HTMLElement>(PAGE_CONTENT_SELECTOR);
    if (!open || !content || typeof ResizeObserver === 'undefined') {
      return undefined;
    }
    let lastWidth: number | null = null;
    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentBoxSize?.[0]?.inlineSize;
      if (width === undefined) return;
      if (lastWidth !== null && width !== lastWidth) {
        window.dispatchEvent(new Event('resize'));
      }
      lastWidth = width;
    });
    observer.observe(content);
    return () => {
      window.setTimeout(() => observer.disconnect(), PUSH_SETTLE_MS);
    };
  }, [open]);

  return open ? PAGE_CONTENT_SELECTOR : undefined;
}
